/**
 * Unsaved skill-editor text, per skill, held in memory above the Chat/Agents
 * switch so it survives Close, Refresh and the switch. Never written to
 * localStorage or sessionStorage: a refused paste may hold client data.
 *
 * Only unsaved text is kept: an entry equal to its baseline (the person's own
 * draft, else the live text) is dropped, so a clean editor follows the baseline.
 */
export type SkillEdits = Readonly<Record<string, string>>;

/** Change the edits with a pure update (React's state setter fits). */
export type UpdateSkillEdits = (
  change: (edits: SkillEdits) => SkillEdits,
) => void;

export const skillKey = (agent: string, skill: string): string =>
  `${agent}/${skill}`;

const has = (edits: SkillEdits, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(edits, key);

/** What the editor shows: the unsaved text, else the baseline. */
export const editorText = (
  edits: SkillEdits,
  key: string,
  baseline: string,
): string => (has(edits, key) ? edits[key] : baseline);

export const withoutEdit = (edits: SkillEdits, key: string): SkillEdits => {
  if (!has(edits, key)) return edits;
  const next = { ...edits };
  delete next[key];
  return next;
};

/** Record the editor text; text equal to the baseline isn't unsaved. Same object when nothing changes. */
export const withEdit = (
  edits: SkillEdits,
  key: string,
  text: string,
  baseline: string,
): SkillEdits => {
  if (text === baseline) return withoutEdit(edits, key);
  if (has(edits, key) && edits[key] === text) return edits;
  return { ...edits, [key]: text };
};

export const hasUnsaved = (edits: SkillEdits): boolean =>
  Object.keys(edits).length > 0;

/** The agent's skills with unsaved text, in the order given. */
export const unsavedSkills = (
  agent: string,
  skills: string[],
  edits: SkillEdits,
): string[] => skills.filter((s) => has(edits, skillKey(agent, s)));
